"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { AlertDialogWrapper } from "@components/common/containers/alert-dialog-wrapper";
import { Label } from "@components/ui/label";
import { Input } from "@components/ui/input";
import { Textarea } from "@components/ui/textarea";
import type { PersonalAccessToken } from "@features/personal-access-tokens/types";
import { classifyToken } from "@features/personal-access-tokens/personal-access-token-view-model";
export type PersonalAccessTokenOwnerAction = "rename" | "revoke" | "delete";
interface PersonalAccessTokenActionDialogsProps {
  readonly token: PersonalAccessToken | null;
  readonly action: PersonalAccessTokenOwnerAction | null;
  readonly isPending: boolean;
  readonly onClose: () => void;
  readonly onRename: (id: number, name: string) => Promise<void>;
  readonly onRevoke: (id: number, reason?: string) => Promise<"already-revoked" | void>;
  readonly onAlreadyRevoked: () => void;
  readonly onDelete: (id: number) => Promise<void>;
}
const PersonalAccessTokenRenameDialog = t0 => {
  const $ = _c(47);
  const {
    token,
    isPending,
    onClose,
    onRename
  } = t0;
  const t = useTranslations("features.personal-access-tokens.ui.ownerTokensPage.dialogs");
  const [name, setName] = useState(token.name);
  const [hasError, setHasError] = useState(false);
  let t1;
  if ($[0] !== name || $[1] !== onClose || $[2] !== onRename || $[3] !== token.id) {
    t1 = async () => {
      setHasError(false);
      try {
        await onRename(token.id, name.trim());
        onClose();
      } catch {
        setHasError(true);
        throw new Error("Personal access token rename failed");
      }
    };
    $[0] = name;
    $[1] = onClose;
    $[2] = onRename;
    $[3] = token.id;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleRename = t1;
  let t2;
  if ($[5] !== t) {
    t2 = t("rename.title");
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t) {
    t3 = t("rename.description");
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t) {
    t4 = t("rename.confirm");
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== isPending || $[12] !== name) {
    t5 = isPending || name.trim().length === 0 || name.length > 128;
    $[11] = isPending;
    $[12] = name;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t) {
    t6 = t("cancel");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== handleRename) {
    t7 = {
      onAction: handleRename
    };
    $[16] = handleRename;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== onClose) {
    t8 = open => {
      if (!open) {
        onClose();
      }
    };
    $[18] = onClose;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = `pat-rename-${token.id}`;
  let t10;
  if ($[20] !== t) {
    t10 = t("rename.nameLabel");
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <Label htmlFor={t9}>{t10}</Label>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = `pat-rename-${token.id}`;
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = event => setName(event.target.value);
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== isPending || $[27] !== name || $[28] !== t12) {
    t14 = <Input id={t12} value={name} maxLength={128} disabled={isPending} onChange={t13} />;
    $[26] = isPending;
    $[27] = name;
    $[28] = t12;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== hasError || $[31] !== t) {
    t15 = hasError && <p role="alert" className="text-sm text-destructive">{t("operationError")}</p>;
    $[30] = hasError;
    $[31] = t;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t11 || $[34] !== t14 || $[35] !== t15) {
    t16 = <div className="grid gap-2">{t11}{t14}{t15}</div>;
    $[33] = t11;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== isPending || $[38] !== t16 || $[39] !== t2 || $[40] !== t3 || $[41] !== t4 || $[42] !== t5 || $[43] !== t6 || $[44] !== t7 || $[45] !== t8) {
    t17 = <AlertDialogWrapper isOpen={true} title={t2} description={t3} actionTitle={t4} actionDisabled={t5} closeTitle={t6} closeDisabled={isPending} handlers={t7} setIsOpenCallback={t8}>{t16}</AlertDialogWrapper>;
    $[37] = isPending;
    $[38] = t16;
    $[39] = t2;
    $[40] = t3;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  return t17;
};
const PersonalAccessTokenConfirmDialog = ({
  token,
  action,
  isPending,
  onClose,
  onRevoke,
  onAlreadyRevoked,
  onDelete
}: Omit<PersonalAccessTokenActionDialogsProps, "onRename"> & {
  readonly token: PersonalAccessToken;
  readonly action: "revoke" | "delete";
}) => {
  const t = useTranslations("features.personal-access-tokens.ui.ownerTokensPage.dialogs");
  const [hasError, setHasError] = useState(false);
  const [reason, setReason] = useState("");
  const isDelete = action === "delete";
  const handleConfirm = async () => {
    setHasError(false);
    try {
      if (isDelete) {
        await onDelete(token.id);
      } else {
        const result = await onRevoke(token.id, reason.trim() || undefined);
        if (result === "already-revoked") onAlreadyRevoked();
      }
      onClose();
    } catch {
      setHasError(true);
      throw new Error(`Personal access token ${action} failed`);
    }
  };
  return <AlertDialogWrapper isOpen title={t(`${action}.title`)} description={t(`${action}.description`)} actionTitle={t(`${action}.confirm`)} actionDisabled={isPending} closeTitle={t("cancel")} closeDisabled={isPending} focusCloseButtonOnMobile handlers={{
    onAction: handleConfirm
  }} setIsOpenCallback={open => {
    if (!open) onClose();
  }}>
      <div className="space-y-3">
        {!isDelete && <div className="grid gap-2">
            <Label htmlFor={`pat-revoke-reason-${token.id}`}>{t("revoke.reasonLabel")}</Label>
            <Textarea id={`pat-revoke-reason-${token.id}`} value={reason} disabled={isPending} onChange={event => setReason(event.target.value)} />
          </div>}
        {isDelete && classifyToken(token) === "working" && <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            {t("delete.activeWarning")}
          </p>}
        {hasError && <p role="alert" className="text-sm text-destructive">
            {t("operationError")}
          </p>}
      </div>
    </AlertDialogWrapper>;
};
export const PersonalAccessTokenActionDialogs = props => {
  const $ = _c(15);
  const {
    token,
    action
  } = props;
  if (!token || !action) {
    return null;
  }
  if (action === "rename") {
    const t0 = `${token.id}-rename`;
    let t1;
    if ($[0] !== props.isPending || $[1] !== props.onClose || $[2] !== props.onRename || $[3] !== t0 || $[4] !== token) {
      t1 = <PersonalAccessTokenRenameDialog key={t0} token={token} isPending={props.isPending} onClose={props.onClose} onRename={props.onRename} />;
      $[0] = props.isPending;
      $[1] = props.onClose;
      $[2] = props.onRename;
      $[3] = t0;
      $[4] = token;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  const t0 = `${token.id}-${action}`;
  let t1;
  if ($[6] !== action || $[7] !== props.isPending || $[8] !== props.onAlreadyRevoked || $[9] !== props.onClose || $[10] !== props.onDelete || $[11] !== props.onRevoke || $[12] !== t0 || $[13] !== token) {
    t1 = <PersonalAccessTokenConfirmDialog key={t0} token={token} action={action} isPending={props.isPending} onClose={props.onClose} onRevoke={props.onRevoke} onAlreadyRevoked={props.onAlreadyRevoked} onDelete={props.onDelete} />;
    $[6] = action;
    $[7] = props.isPending;
    $[8] = props.onAlreadyRevoked;
    $[9] = props.onClose;
    $[10] = props.onDelete;
    $[11] = props.onRevoke;
    $[12] = t0;
    $[13] = token;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  return t1;
};